/* ---------------------------------------------------------------------------
   Every user-visible string that describes a contract value lives here.

   Two reasons, and the second is specific to this surface.

   First, the product is white-labelled, so a string a user can read has to be
   auditable in our own repository rather than inherited from a component
   registry or improvised at a call site. Second, this module is the single
   place to check when the contract's enums change: a new status without an
   entry here is a compile error at the lookup, not a raw wire value leaking
   onto a screen.

   TONE. This is the one dashboard whose reader is technical and inside the
   trust boundary. Say what is true in the vocabulary of the thing: repos,
   SHAs, branches, merges. The client dashboard's plain-language translation
   would be condescending here and, worse, would hide the detail a reviewer
   needs to decide. No apologies, no exclamation marks.
--------------------------------------------------------------------------- */
import type {
  ActorKind,
  AncestryCheck,
  ChangeKind,
  ChatStatus,
  ClientChatStatus,
  ComposerReasonCode,
  DeployEnvironment,
  DeployStatus,
  PreflightRepoStatus,
  PromotionBlockCode,
  PromotionMergeStatus,
  PromotionPreflightStatus,
  PromotionStatus,
  PromotionTarget,
  PushStatus,
  RunPhase,
  RunStatus,
  ToolFamily,
  Visibility,
} from '@/types/api';

export type Tone = 'neutral' | 'active' | 'ok' | 'warn' | 'danger' | 'hidden';

export interface StateCopy {
  label: string;
  tone: Tone;
  /** One sentence a reviewer can act on. Shown in tooltips and fact rows. */
  detail?: string;
}

// -- chat status ------------------------------------------------------------

export const CHAT_STATUS: Record<ChatStatus, StateCopy> = {
  draft: {
    label: 'Draft',
    tone: 'neutral',
    detail: 'The client may prompt. Not in review — a claim on this chat will be refused.',
  },
  'agent running': { label: 'Running', tone: 'active', detail: 'A run is executing on this project.' },
  'agent failed': {
    label: 'Run failed',
    tone: 'danger',
    detail: 'The run failed. An expert-actor failure returns here to pending review, never to draft.',
  },
  deploying: { label: 'Deploying', tone: 'active', detail: 'The push succeeded and a pre-staging deploy is in flight.' },
  'deploy failed': { label: 'Deploy failed', tone: 'danger', detail: 'The deploy failed, or failed platform-side verification.' },
  'pending review': {
    label: 'Pending review',
    tone: 'warn',
    detail:
      'The client pressed Done. The project is frozen. This one status covers a queued chat, a checked-out chat and a chat resting between review turns — the claim is what tells them apart.',
  },
  'changes requested': {
    label: 'Changes requested',
    tone: 'neutral',
    detail: 'Handed back to the client. The project is unfrozen and the claim is released.',
  },
  'merged to staging': {
    label: 'Merged to staging',
    tone: 'ok',
    detail:
      'Staging merge verified. This leaves review and UNFREEZES the project — the client can prompt again while production is still pending.',
  },
  'merged to production': { label: 'Merged to production', tone: 'ok', detail: 'Production merge verified. The change is live.' },
  'promotion failed': {
    label: 'Promotion failed',
    tone: 'danger',
    detail: 'A sequential merge died mid-sequence. Some repos merged, some did not. No automated recovery exists.',
  },
};

/** What the CLIENT is currently being shown. Rendered beside the true status. */
export const CLIENT_CHAT_STATUS: Record<ClientChatStatus, string> = {
  draft: 'Draft',
  'agent running': 'Running',
  'agent failed': 'Run failed',
  deploying: 'Deploying',
  'deploy failed': 'Deploy failed',
  'pending review': 'Pending review',
  'changes requested': 'Changes requested',
};

/**
 * Why the client is seeing that projected status, when the server does not send
 * its own note. The projection pins the client's view across the whole review
 * pipeline precisely so a client cannot infer the existence or the timing of the
 * hidden turns from a flickering status.
 */
export function clientStatusNote(status: ChatStatus, clientStatus: ClientChatStatus): string {
  if (status === clientStatus) return 'The client sees the real status here — this one is not projected.';
  if (clientStatus === 'pending review') {
    return 'Projected. Your work passes through statuses the client never sees, so their view is pinned to pending review for the whole of the review.';
  }
  if (clientStatus === 'draft') {
    return 'Projected. Review is over and the project is unfrozen, so the client sees an open composer.';
  }
  return 'Projected. The client is shown a different status from the one this chat is actually in.';
}

// -- actors and visibility --------------------------------------------------

export const ACTOR_KIND: Record<ActorKind, string> = {
  client: 'Client',
  expert: 'You',
  system: 'Platform',
};

export const VISIBILITY: Record<Visibility, StateCopy> = {
  client: {
    label: 'Client sees this',
    tone: 'neutral',
    detail: 'This turn is on the client’s thread exactly as it is here.',
  },
  expert: {
    label: 'Hidden from client',
    tone: 'hidden',
    detail:
      'Expert-visibility turn. It is filtered out server-side on every client read path and never appears on the client stream (invariant I7).',
  },
  internal: {
    label: 'Hidden from client',
    tone: 'hidden',
    detail:
      'Internal-visibility turn — tool calls, thinking, operational notices. Never reaches any client surface.',
  },
};

// -- runs -------------------------------------------------------------------

export const RUN_STATUS: Record<RunStatus, StateCopy> = {
  queued: { label: 'Queued', tone: 'neutral' },
  running: { label: 'Running', tone: 'active' },
  succeeded: { label: 'Succeeded', tone: 'ok' },
  failed: { label: 'Failed', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  expired: {
    label: 'Expired',
    tone: 'danger',
    detail: 'The reaper failed this run: its lease lapsed because the goroutine died. A platform fault, not an agent one.',
  },
};

/** The neutral progress projection, in the order the rail draws its notches. */
export const RUN_PHASE_ORDER: RunPhase[] = [
  'queued',
  'preparing',
  'working',
  'committing',
  'deploying',
  'verifying',
  'finished',
];

export const RUN_PHASE: Record<RunPhase, string> = {
  queued: 'Queued',
  preparing: 'Preparing workspace',
  working: 'Working',
  committing: 'Committing',
  deploying: 'Deploying',
  verifying: 'Verifying',
  finished: 'Finished',
  failed: 'Failed',
};

export const PUSH_STATUS: Record<PushStatus, StateCopy> = {
  not_needed: { label: 'Unchanged', tone: 'neutral', detail: 'This repo’s SHA did not advance, so it is outside the deploy scope.' },
  pending: { label: 'Pending', tone: 'active' },
  pushed: { label: 'Pushed', tone: 'ok' },
  failed: { label: 'Push failed', tone: 'danger' },
};

export const TOOL_FAMILY: Record<ToolFamily, { label: string; detail: string }> = {
  read: { label: 'read', detail: 'Read a file from the workspace.' },
  edit: { label: 'edit', detail: 'Wrote to a file in the workspace.' },
  shell: { label: 'shell', detail: 'Ran a command inside the sandbox.' },
  search: { label: 'search', detail: 'Searched the workspace.' },
  net: { label: 'net', detail: 'Reached the network through the egress allowlist.' },
  task: { label: 'task', detail: 'Delegated a sub-task.' },
  ask: { label: 'ask', detail: 'Asked a question and parked.' },
  other: { label: 'other', detail: 'A tool outside the neutral families. Switch on family, never on the vendor label.' },
};

// -- deploys ----------------------------------------------------------------

export const DEPLOY_STATUS: Record<DeployStatus, StateCopy> = {
  queued: {
    label: 'Queued',
    tone: 'neutral',
    detail: 'Waiting on a build slot. The target server runs two concurrent builds shared with every other tenant.',
  },
  building: { label: 'Building', tone: 'active' },
  verifying: {
    label: 'Verifying',
    tone: 'active',
    detail: 'The provider reported success. The platform is now checking the deployed commit and probing the host.',
  },
  succeeded: {
    label: 'Live',
    tone: 'ok',
    detail:
      'All three post-checks passed: the deployed commit matches the pushed SHA, the provider reported success, and the platform-side HTTPS probe answered.',
  },
  failed: { label: 'Failed', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export const ENVIRONMENT: Record<DeployEnvironment, { label: string; short: string; detail: string }> = {
  'pre-staging': { label: 'Pre-staging', short: 'pre', detail: 'The only environment a run can reach. This is what you are reviewing.' },
  staging: { label: 'Staging', short: 'stg', detail: 'First promotion target. Reached only through a promotion.' },
  production: { label: 'Production', short: 'prod', detail: 'Second promotion target, gated on a per-repo git ancestry check across every repo.' },
};

export const ENVIRONMENT_ORDER: DeployEnvironment[] = ['pre-staging', 'staging', 'production'];

// -- diff -------------------------------------------------------------------

export const CHANGE_KIND: Record<ChangeKind, { label: string; short: string; tone: Tone }> = {
  added: { label: 'Added', short: 'A', tone: 'ok' },
  modified: { label: 'Modified', short: 'M', tone: 'neutral' },
  deleted: { label: 'Deleted', short: 'D', tone: 'danger' },
  renamed: { label: 'Renamed', short: 'R', tone: 'warn' },
  copied: { label: 'Copied', short: 'C', tone: 'warn' },
  type_changed: { label: 'Type changed', short: 'T', tone: 'warn' },
};

// -- promotion --------------------------------------------------------------

export const PROMOTION_TARGET: Record<PromotionTarget, { label: string; verb: string; detail: string }> = {
  staging: {
    label: 'Staging',
    verb: 'Push to staging',
    detail:
      'Merges pre-staging into staging for every repo in position order, one fully deployed before the next begins. On success the chat reaches merged to staging, which leaves review and unfreezes the project.',
  },
  production: {
    label: 'Production',
    verb: 'Push to production',
    detail:
      'Same sequential machinery, plus the per-repo git ancestry check across all repos. Every repo must carry a recorded staging merge SHA that is an ancestor of its staging head.',
  },
};

export const PROMOTION_STATUS: Record<PromotionStatus, StateCopy> = {
  preflight: { label: 'Pre-flight', tone: 'active', detail: 'Fetching and merge-tree-ing every repo. Nothing has been written.' },
  merging: { label: 'Merging', tone: 'active' },
  deploying: { label: 'Deploying', tone: 'active', detail: 'A repo merged and its deploy is being verified before the sequence advances.' },
  succeeded: { label: 'Succeeded', tone: 'ok' },
  failed: { label: 'Failed', tone: 'danger', detail: 'The sequence died partway. Some repos merged, some did not.' },
};

export const PREFLIGHT_REPO_STATUS: Record<PreflightRepoStatus, StateCopy> = {
  clean: { label: 'Clean', tone: 'ok', detail: 'merge-tree produced a tree with no conflicts.' },
  conflict: { label: 'Conflict', tone: 'danger', detail: 'merge-tree found a conflict. The promotion is refused with nothing merged.' },
  no_changes: { label: 'No changes', tone: 'neutral', detail: 'This repo is already up to date with the target.' },
  blocked: { label: 'Blocked', tone: 'danger', detail: 'Something other than a conflict refuses this repo — see the blocking reasons.' },
};

export const PROMOTION_PREFLIGHT_STATUS: Record<PromotionPreflightStatus, StateCopy> = {
  pending: { label: 'Pending', tone: 'neutral' },
  clean: { label: 'Pre-flight ok', tone: 'ok' },
  conflict: { label: 'Conflict', tone: 'danger' },
  skipped: { label: 'Skipped', tone: 'neutral' },
};

export const PROMOTION_MERGE_STATUS: Record<PromotionMergeStatus, StateCopy> = {
  pending: { label: 'Pending', tone: 'neutral' },
  merged: { label: 'Merged', tone: 'ok' },
  failed: { label: 'Failed', tone: 'danger' },
  skipped: { label: 'Skipped', tone: 'neutral', detail: 'Nothing to merge — this repo was already at the target.' },
};

export const ANCESTRY_REASON: Record<NonNullable<AncestryCheck['reason']>, string> = {
  ok: 'The recorded staging merge SHA is an ancestor of this repo’s staging head.',
  no_recorded_merge: 'No staging merge SHA is recorded for this repo. The gate fails closed — this blocks the whole promotion.',
  not_ancestor: 'The recorded staging merge SHA is not an ancestor of staging head. Something moved staging outside the platform.',
  ref_missing: 'The staging ref could not be read for this repo.',
};

export const PROMOTION_BLOCK: Record<PromotionBlockCode, StateCopy> = {
  preflight_conflict: {
    label: 'Merge conflict',
    tone: 'danger',
    detail: 'merge-tree found a conflict. The conflicting paths are named per repo below. Nothing has been merged.',
  },
  preflight_stale: {
    label: 'Pre-flight stale',
    tone: 'warn',
    detail: 'The refs moved since the token was issued — someone pushed. Re-run pre-flight; do not retry with the old token.',
  },
  staging_ancestry_missing: {
    label: 'Staging ancestry missing',
    tone: 'danger',
    detail:
      'Production only. A repo has no recorded staging merge SHA, or it is not an ancestor of staging head. The gate is computed in git and fails closed (invariant I3).',
  },
  protection_not_enforced: {
    label: 'Branch protection not enforced',
    tone: 'danger',
    detail:
      'Protection could not be applied to a repo, so platform Go code is the only thing standing between arbitrary code and production. That is not enough to promote on.',
  },
  promotion_in_flight: { label: 'Promotion in flight', tone: 'warn', detail: 'One promotion per project at a time.' },
  run_in_progress: {
    label: 'Run in flight',
    tone: 'warn',
    detail:
      'A run and a promotion are mutually exclusive per project. A push to pre-staging during a sequential merge would make the merge ship a SHA that is no longer the head.',
  },
  not_checked_out: { label: 'Not checked out', tone: 'warn', detail: 'Claim this chat before promoting.' },
  not_in_review: { label: 'Not in review', tone: 'warn', detail: 'This chat is not in a promotable state.' },
  no_changes: { label: 'No changes', tone: 'neutral', detail: 'Every repo is already up to date with the target.' },
  provisioning_incomplete: {
    label: 'Provisioning incomplete',
    tone: 'warn',
    detail: 'The repos or their applications are not fully provisioned. An operator re-runs the saga.',
  },
  project_held: { label: 'Project held', tone: 'warn', detail: 'An active hold on this project. An operator releases it.' },
  repo_held: { label: 'Repo held', tone: 'warn', detail: 'An active hold on a repo of this project.' },
  expert_held: { label: 'You are held', tone: 'warn', detail: 'An active hold on your account. An operator releases it.' },
};

// -- the composer predicate -------------------------------------------------

/**
 * Why the expert composer is closed.
 *
 * `CanPrompt(chat, actor)` produces exactly one of these codes, and the same
 * value greys the composer, fills the 409 body and gates the run at start — so
 * the code is never ambiguous and only the wording is ours. Render a specific
 * string per code: a generic "you can't send this right now" wastes the one
 * piece of information the predicate exists to carry.
 *
 * Several codes in the contract's enum apply only to a client actor. They are
 * mapped anyway, in the expert's voice, because a code arriving unexpectedly
 * should still produce a truthful sentence rather than a drift report.
 */
export const COMPOSER_REASON: Record<ComposerReasonCode, StateCopy> = {
  ok: { label: 'Ready', tone: 'ok', detail: 'Prompt the agent in this thread. Your turns are never visible to the client.' },
  run_in_progress: { label: 'Run in flight', tone: 'active', detail: 'One run per project. The composer reopens when it finishes.' },
  awaiting_answer: { label: 'Question parked', tone: 'warn', detail: 'The agent is waiting on an answer. Answer it in the thread and the run continues.' },
  promotion_in_flight: {
    label: 'Promotion in flight',
    tone: 'active',
    detail: 'A sequential merge is running. A run now would push to pre-staging and make the merge ship a stale SHA.',
  },
  not_checked_out: { label: 'Not checked out', tone: 'warn', detail: 'Claim this chat to prompt in it. Claiming is a compare-and-set; it moves no status.' },
  claimed_by_other: { label: 'Claimed by another expert', tone: 'warn', detail: 'Their lease has to expire or they have to release it.' },
  not_in_review: { label: 'Not in review', tone: 'neutral', detail: 'This chat is outside the review pipeline. It is the client’s thread right now.' },
  expert_held: { label: 'You are held', tone: 'warn', detail: 'A hold on your account blocks claiming, prompting and promoting. An operator releases it.' },
  project_held: { label: 'Project held', tone: 'warn', detail: 'A hold on this project. Nothing is deleted; an operator releases it.' },
  repo_held: { label: 'Repo held', tone: 'warn', detail: 'A hold on a repo of this project.' },
  context_exhausted: {
    label: 'Context exhausted',
    tone: 'warn',
    detail:
      'This chat hit the 1M ceiling. The composer locks for both roles, and Done and every promotion action stay available — freeze, never destroy.',
  },
  provisioning_incomplete: { label: 'Provisioning incomplete', tone: 'warn', detail: 'There is no verified infrastructure to run against.' },
  not_assigned: { label: 'Not assigned', tone: 'warn', detail: 'You have no live assignment to this project. An operator assigns it.' },
  chat_completed: { label: 'Completed', tone: 'ok', detail: 'This change reached production. There is nothing left to review.' },
  project_frozen: { label: 'Project frozen', tone: 'neutral', detail: 'The freeze blocks the client, not you.' },
  pending_review: { label: 'Pending review', tone: 'neutral', detail: 'A client-side code. It does not block an expert actor.' },
  usage_limit_reached: { label: 'Usage limit reached', tone: 'warn', detail: 'The client’s subscription hit a provider usage limit. It clears on its own.' },
  subscription_not_connected: { label: 'Subscription not connected', tone: 'warn', detail: 'No credential is stored for this client. An operator connects it.' },
  subscription_reauth_required: { label: 'Subscription needs re-auth', tone: 'warn', detail: 'The stored credential needs re-authenticating. An operator does it in one click.' },
  subscription_held: { label: 'Subscription held', tone: 'warn', detail: 'Administrative hold on the credential. An operator releases it.' },
  client_held: { label: 'Client held', tone: 'warn', detail: 'A hold on the client. It does not block your review.' },
  no_successful_deploy: { label: 'No verified deploy', tone: 'warn', detail: 'A client-side code: Done needs a verified deploy behind it.' },
  already_pending_review: { label: 'Already in review', tone: 'neutral', detail: 'A client-side code: Done was pressed twice.' },
};

// -- API failures -----------------------------------------------------------

/** Wording for the failure codes this dashboard can provoke. Anything not
    listed falls back to the server's own `message`, which the contract
    guarantees is safe to display. */
export const ERROR_COPY: Record<string, string> = {
  network_unreachable: 'The API did not answer. Check that the platform binary is running.',
  unauthenticated: 'Your session has ended. Sign in again.',
  wrong_role: 'That account is not an expert. Sign in with the Google account your operator set up.',
  claimed_by_other: 'Another expert holds this chat. Their lease has to expire, or they have to release it.',
  not_pending_review: 'This chat is not in pending review, so it cannot be claimed.',
  expert_held: 'A hold on your account blocks claiming, prompting and promoting. An operator releases it.',
  not_checked_out: 'Claim this chat before doing that.',
  run_in_progress: 'A run is in flight on this project.',
  promotion_in_flight: 'A promotion is already running on this project.',
  preflight_stale: 'The refs moved since pre-flight ran. Re-run it — do not retry with the old token.',
  mirror_fetch_failed: 'A mirror fetch failed, so the diff would be computed against a stale ref. Retry in a moment.',
  provisioning_incomplete: 'This project’s repos are not provisioned. An operator re-runs the saga.',
  malformed_response: 'The API returned a response this console could not read.',
};

export function errorMessage(code: string, fallback: string): string {
  return ERROR_COPY[code] ?? fallback;
}

/** The sentence that appears wherever `promotion failed` is rendered. It is the
    one state in the whole product with no automated recovery, and saying so
    plainly is the difference between an operator paging and an expert retrying
    a button that will never work. */
export const PROMOTION_FAILED_MEANING =
  'A sequential merge died partway. Some repos are merged and some are not, and the platform does not compensate by reverting — it stops, records exactly where, and pages an operator.';

export const PROMOTION_FAILED_RECOVERY =
  'Repair happens outside the platform: fix the repos by hand on GitHub, run the reconcile tool so the database matches what GitHub and the deployment host actually hold, then an operator returns this chat to pending review from the operator console. There is no button here that does it.';
